<template>
  <el-dialog
    :title="title"
    :visible="visible"
    width="420px"
    append-to-body
    custom-class="qr-dialog"
    @close="handleClose"
  >
    <div class="qr-body" v-loading="loading" element-loading-text="生成中">
      <!-- 人员信息 -->
      <div class="qr-person">
        <div class="qr-name">{{ personName }}</div>
        <div class="qr-meta">
          <span v-if="shownPosition">{{ shownPosition }}</span>
          <span v-if="shownPosition && companyName" class="dot">·</span>
          <span v-if="companyName">{{ companyName }}</span>
        </div>
      </div>

      <!-- 二维码：接口下发 base64 直出；接口不可用时退回前端本地绘制 -->
      <div class="qr-canvas-wrap">
        <img v-if="qrImage" :src="qrImage" class="qr-img" alt="分享二维码" />
        <canvas v-else ref="canvas" class="qr-canvas"></canvas>
        <div v-if="errorMsg" class="qr-error">{{ errorMsg }}</div>
        <!-- 中心小标识：遮挡面积远小于 30%，纠错等级 M 足够还原 -->
        <div v-if="qrImage && !errorMsg" class="qr-logo">国塔</div>
      </div>

      <div class="qr-hint">扫一扫，进入圣山塔林小程序</div>
    </div>

    <div slot="footer">
      <el-button size="small" @click="handleClose">关 闭</el-button>
      <el-button size="small" type="primary" :disabled="!!errorMsg || loading" @click="download">下载二维码</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { drawQrToCanvas } from '@/utils/qrcode'
import { getPersonShareQr } from '@/api/system'

// 接口取不到时（后端未更新 / 生成失败）的兜底链接口径，须与后端 share_qr 配置保持一致
const FALLBACK_BASE_URL = 'https://talin.api.xywh-yuanqi.com'

export default {
  name: 'PersonQrDialog',
  props: {
    visible: { type: Boolean, default: false },
    // 弹窗标题：人员管理页叫「分享二维码」，顶栏入口叫「推广二维码」，同一功能两种叫法
    title: { type: String, default: '分享二维码' },
    person: { type: Object, default: () => ({}) },
    companyName: { type: String, default: '' },
    positionLabel: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      errorMsg: '',
      // 后端下发的 data URI；为空表示走本地兜底绘制
      qrImage: '',
      // 后端下发的推广链接；为空用本地拼的
      remoteUrl: '',
      // 接口下发的姓名/岗位（顶栏进「推广二维码」时只有人员 id，没有姓名岗位，靠这里补）
      remoteName: '',
      remotePosition: ''
    }
  },
  computed: {
    // 外层一律走 computed，不直接 person.xxx：父组件偶发传 undefined 时 render 不能崩
    personName() {
      const p = this.person || {}
      return this.remoteName || p.name || '—'
    },
    // 岗位文案：接口给的优先，其次父组件传的
    shownPosition() {
      return this.remotePosition || this.positionLabel || ''
    },
    personId() {
      const p = this.person || {}
      return Number(p.id) || 0
    },
    // 分享链接：referrer 参数即人员 ID，与扫码日志的 share_user_id 口径一致
    shareUrl() {
      if (this.remoteUrl) return this.remoteUrl
      return `${FALLBACK_BASE_URL}?referrer=${this.personId}`
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.load()
      } else {
        this.reset()
      }
    }
  },
  methods: {
    reset() {
      this.qrImage = ''
      this.remoteUrl = ''
      this.remoteName = ''
      this.remotePosition = ''
      this.errorMsg = ''
      this.loading = false
    },

    /**
     * 取二维码：优先走后端 GET /system/person/{id}/share-qr
     * 后端不可用（404/网络错/返回空图）时退回前端本地绘制，保证页面可用
     */
    async load() {
      const id = this.personId
      if (!id) {
        this.errorMsg = '人员 ID 缺失'
        return
      }
      this.reset()
      this.loading = true
      try {
        const res = await getPersonShareQr(id)
        // 项目拦截器 return data，业务体在 res.data
        const body = (res && res.data) || {}
        this.remoteUrl = body.url || ''
        this.remoteName = body.name || ''
        this.remotePosition = body.position_text || ''
        if (body.qrcode) {
          this.qrImage = body.qrcode
          return
        }
        // 后端明确返回了失败原因，直接展示，不再兜底画一个"能用但口径可能不对"的码
        if (body.qrcode_error) {
          this.errorMsg = `二维码生成失败：${body.qrcode_error}`
          return
        }
        // 接口通了但没图没原因 → 兜底本地绘制
        this.renderLocal()
      } catch (e) {
        // 接口不存在或网络异常 → 兜底本地绘制（口径与后端一致，仅绘制端不同）
        this.renderLocal()
      } finally {
        this.loading = false
      }
    },

    /** 前端本地绘制（兜底）：与后端同一套链接口径 */
    renderLocal() {
      this.errorMsg = ''
      this.$nextTick(() => {
        const canvas = this.$refs.canvas
        if (!canvas) {
          this.errorMsg = '二维码生成失败'
          return
        }
        try {
          drawQrToCanvas(canvas, this.shareUrl, {
            size: 240,
            margin: 2,
            ecLevel: 'M',
            dark: '#1a1a1a',
            light: '#ffffff'
          })
        } catch (e) {
          this.errorMsg = e && e.message ? e.message : '二维码生成失败'
        }
      })
    },

    download() {
      const name = (this.personName || 'person').replace(/[\\/:*?"<>|\s]/g, '')
      const url = this.qrImage || (this.$refs.canvas && this.$refs.canvas.toDataURL('image/png'))
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.download = `分享二维码-${name}.png`
      a.click()
    },

    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
/* 注意：这里刻意用纯 CSS，不用 lang="less"。
   构建机 Node 12，而项目里的 less 4.5.1 源码含 ?? 空值合并语法，
   Node 12 解析直接抛 SyntaxError: Unexpected token '?'，整个 build 会挂。
   本组件样式不依赖 less 特性（无变量、无嵌套），写纯 CSS 等价。 */
.qr-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0 8px;
  min-height: 380px;
}

.qr-person {
  text-align: center;
  margin-bottom: 16px;
}

.qr-name {
  font-size: 17px;
  font-weight: 600;
  color: #1d2129;
  line-height: 24px;
}

.qr-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #86909c;
}

.qr-meta .dot {
  margin: 0 6px;
}

.qr-canvas-wrap {
  position: relative;
  padding: 10px;
  background: #fff;
  border: 1px solid #f0f1f3;
  border-radius: 12px;
  line-height: 0;
}

.qr-canvas {
  display: block;
  width: 240px;
  height: 240px;
}

.qr-img {
  display: block;
  width: 240px;
  height: 240px;
  object-fit: contain;
}

/* 中心标识：白底圆角小方块，压在二维码正中 */
.qr-logo {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 44px;
  height: 44px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #8e1103;
  line-height: 1;
  box-shadow: 0 0 0 3px #fff;
}

.qr-error {
  width: 240px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f53f3f;
  font-size: 13px;
  line-height: 1.6;
  text-align: center;
  padding: 0 16px;
  box-sizing: border-box;
}

.qr-hint {
  margin-top: 12px;
  font-size: 12px;
  color: #86909c;
}
</style>
